import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import authService from "../../../services/authService";
import logo from '../../../assets/icons/logo/Logo.svg';
import './styles.scss';

export default function ForgotPassword({
    customMessages = {
        wrongEmail: "Wrong email",
        invalidEmail: "Invalid email address",
        required: "Email is required"
    }
}) {
    const [searchParams] = useSearchParams();
    const urlEmail = searchParams.get('email') || "";
    const navigate = useNavigate();
    
    const [isLoading, setIsLoading] = useState(false);
    const [wrongEmailError, setWrongEmailError] = useState(false);
    const [serverError, setServerError] = useState("");

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        watch
    } = useForm({
        defaultValues: {
            email: urlEmail
        }
    });

    const email = watch("email");

    useEffect(() => {
        if (urlEmail) {
            setValue("email", urlEmail);
        }
    }, [urlEmail, setValue]);

    const onSubmit = async (data) => {
        setWrongEmailError(false);
        setServerError("");
        
        setIsLoading(true);

        try {
            await authService.forgotPassword(data.email);

            navigate('/verification-code', { 
                state: { 
                    email: data.email 
                } 
            });
        } catch (error) {
            if (
                error?.response?.data?.error &&
                error.response.data.error.toLowerCase().includes("user not found")
            ) {
                setWrongEmailError(true);
            } else {
                setServerError(
                    error?.response?.data?.error ||
                    "Failed to send verification code. Please try again."
                );
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="forgot-password-wrapper">
            <div className="forgot-password-card">
                <img src={logo} alt="Logo" className="forgot-password-logo" />

                <div className="forgot-password-heading">
                    <h1 className="forgot-password-title">Forgot your password?</h1>
                    <p className="forgot-password-subtitle">
                        Kindly enter the email address linked to this account and we will send you a code to enable you change your password.
                    </p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <div className="email-section">
                        <h3 className="email-section-title">Email Address</h3>

                        <div className="field">
                            <input
                                type="email"
                                placeholder="Enter your email"
                                className={`field-input ${wrongEmailError || errors.email || serverError ? 'field-input-error' : ''}`}
                                {...register("email", {
                                    required: customMessages.required,
                                    pattern: {
                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                        message: customMessages.invalidEmail
                                    },
                                    onChange: () => {
                                        setWrongEmailError(false);
                                        setServerError("");
                                    }
                                })}
                                disabled={isLoading}
                            />
                            {wrongEmailError && (
                                <span className="field-error wrong-email-error">
                                    {customMessages.wrongEmail}
                                </span>
                            )}
                            {errors.email && !wrongEmailError && (
                                <span className="field-error">{errors.email.message}</span>
                            )}
                            {serverError && !wrongEmailError && !errors.email && (
                                <span className="field-error">{serverError}</span>
                            )}
                        </div>

                        <div className="field">
                            <button 
                                type="submit" 
                                className="btn-submit"
                                disabled={isLoading}
                            >
                                {isLoading ? "Sending..." : "Send"}
                            </button>
                        </div>
                    </div>
                </form>

                <div className="back-to-login-section">
                    <Link to="/login" className="link-primary">
                        ← Back to Login
                    </Link>
                </div>
            </div>
        </div>
    );
}